/**
 * Signing in to a server, or making an account there. The server field starts with the
 * page's own server (or the pending invite's); the desktop app types any address. An account
 * with the second step on is then asked the code of its authenticator app.
 */
import { Show, createSignal, type JSX } from "solid-js";
import { useApp } from "../../app/context";

/** The sign-in (or sign-up) form. */
export function SignIn(): JSX.Element {
  const { accounts } = useApp();
  const [creating, setCreating] = createSignal(false);
  const [server, setServer] = createSignal(accounts.pending()?.link.server ?? accounts.homeServer ?? "");
  const [email, setEmail] = createSignal("");
  const [name, setName] = createSignal("");
  const [password, setPassword] = createSignal("");
  const [busy, setBusy] = createSignal(false);
  const [code, setCode] = createSignal("");
  /** Whether the server asked this form's sign-in for its second step's code. */
  const asked = () => !!accounts.codeNeeded() && !creating();
  /** Whether this form is the only thing to do (signed in nowhere yet). */
  const first = () => accounts.sessions().length === 0;

  /** Signs in or up; on success the password is forgotten. */
  const submit = async () => {
    setBusy(true);
    const done = creating()
      ? await accounts.signUp(server(), email(), name(), password())
      : await accounts.signIn(server(), email(), password(), asked() ? code() : undefined);
    setBusy(false);
    if (done) {
      setPassword("");
      setCode("");
    }
  };

  return (
    <section class="grp" id="home-signin">
      <h3>{first() ? "Sign in to work with your team" : "Add a server"}</h3>
      <form
        class="home-form"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <label class="small">
          Server
          <input class="field" id="home-server" placeholder="http://192.168.1.4:7878" value={server()} onInput={(e) => setServer(e.currentTarget.value)} />
        </label>
        <label class="small">
          Email
          <input class="field" id="home-email" type="email" autocomplete="username" value={email()} onInput={(e) => setEmail(e.currentTarget.value)} />
        </label>
        <Show when={creating()}>
          <label class="small">
            Name (shown to the others)
            <input class="field" id="home-name" autocomplete="name" value={name()} onInput={(e) => setName(e.currentTarget.value)} />
          </label>
        </Show>
        <label class="small">
          Password{creating() ? " (at least 8 characters)" : ""}
          <input class="field" id="home-password" type="password" autocomplete={creating() ? "new-password" : "current-password"} value={password()} onInput={(e) => setPassword(e.currentTarget.value)} />
        </label>
        <Show when={asked()}>
          <label class="small">
            Code from your authenticator app (or a recovery code)
            <input class="field mono" id="home-code" inputmode="numeric" autocomplete="one-time-code" value={code()} onInput={(e) => setCode(e.currentTarget.value)} />
          </label>
        </Show>
        <span class="btnrow">
          <button type="submit" class="btn primary" id="home-submit" disabled={busy()}>
            {creating() ? "Create account" : "Sign in"}
          </button>
          <button type="button" class="btn" id="home-mode" onClick={() => setCreating(!creating())}>
            {creating() ? "I have an account" : "Create an account"}
          </button>
        </span>
      </form>
    </section>
  );
}
